import React, { useState, useEffect, useCallback } from "react";
import CreateTheme from "../createTheme/createTheme";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../themeSlice";
import { useNavigate } from "react-router-dom";

import Swal from "sweetalert2";
import "./home.css";

import { selectAdminToken } from "../../sessionSlice";
import UpdateThemeImages from "../updateThemeImage/updateThemeImage";
import { apiUrl } from "../../../utils/apiUrl";

function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const adminToken = useSelector(selectAdminToken);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { status, data, error } = useSelector((state) => state.theme);

  const browseTheme = async (themeName) => {
    try {
      await dispatch(fetchThemeData({ themeId: themeName })).unwrap();
      navigate("/admin/rules");
    } catch (err) {
      Swal.fire("Error loading theme", err.message, "error");
    }
  };

  const fetchAllThemes = useCallback(() => {
    if (!adminToken) return;
    setThemesLoading(true);
    fetch(apiUrl("/fetchAllThemes"), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${adminToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then((json) => {
        if (json.success) {
          setThemes(Array.isArray(json.data) ? json.data : []);
        } else {
          console.error("API error:", json.message);
          setThemes([]);
        }
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setThemes([]);
      })
      .finally(() => setThemesLoading(false));
  }, [adminToken]);

  // Seed session themes via fetchThemeData, then load the Select Themes list
  // (fetchAllThemes alone does not seed — racing it on a new session returns empty)
  useEffect(() => {
    if (!adminToken || isUpdateModalOpen) return;
    let cancelled = false;
    dispatch(fetchThemeData({ isAdmin: true }))
      .unwrap()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) fetchAllThemes();
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, adminToken, isUpdateModalOpen, fetchAllThemes]);

  const handleSelectTheme = (themeName) => {
    fetch(apiUrl("/selectTheme"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({
        themeName,
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) {
          console.error("SelectTheme error:", json.message);
        } else {
          Swal.fire({
            title: "Theme Selected",
            text: "",
            icon: "success",
          });
          fetchAllThemes();
          dispatch(fetchThemeData({ isAdmin: true }));
        }
      })
      .catch((err) => console.error("SelectTheme fetch error:", err));
  };

  const deleteTheme = (themeName) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you really want to delete the theme ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(apiUrl("/deleteTheme"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ themeName }),
        })
          .then((res) => res.json())
          .then((json) => {
            if (!json.success) {
              console.error("deleteTheme error:", json.message);
              Swal.fire("Error", json.message, "error");
            } else {
              Swal.fire("Deleted!", `Theme has been deleted.`, "success").then(
                () => {
                  window.location.reload();
                }
              );
            }
          })
          .catch((err) => {
            console.error("deleteTheme fetch error:", err);
            Swal.fire("Error", "Network error, please try again.", "error");
          });
      }
    });
  };

  const OpenUpdateModal = (themeName) => {
    dispatch(fetchThemeData({ themeId: themeName, isAdmin: true }));
    setIsUpdateModalOpen(true);
  };

  if (!adminToken || status === "idle" || status === "loading") {
    return (
      <div className="load-theme-container">
        <div className="load-theme-spinner"></div>
        <p>Loading themes...</p>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="load-theme-container">
        <p className="load-theme-error">❌ Error loading themes{error ? `: ${error}` : ""}</p>
        <button
          type="button"
          className="te-create-btn"
          style={{ marginTop: 12 }}
          onClick={() => dispatch(fetchThemeData({ isAdmin: true }))}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <CreateTheme isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <UpdateThemeImages
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
      />
      <div className="te-container">
        {/* LEFT SIDE (70%) */}
        <div className="te-left">
          <div className="te-image-wrapper">
            {data?.themename ? (
              <img
                src={process.env.REACT_APP_S3_PATH + `${data?.themeImage}`}
                className="te-main-image"
                alt={data?.themeName || "Theme"}
              />
            ) : (
              <>No Theme Found</>
            )}
          </div>

          <div className="te-info">
            <h2 className="te-name">{data?.themeName}</h2>
            <p className="te-desc">{data?.themeDescription}</p>
            {data?.themename ? (
              <div className="te-icons">
                <i
                  className="fa-solid fa-image te-icon"
                  title="Edit Image"
                  onClick={() => OpenUpdateModal(data.themename)}
                ></i>
                <i
                  className="fa-solid fa-gear te-icon"
                  title="Settings"
                  onClick={(e) => {
                    e.stopPropagation();
                    browseTheme(data.themename);
                  }}
                ></i>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>

        {/* RIGHT SIDE (30%) */}
        <div className="te-right">
          <div className="te-select-header">
            <h3 className="te-select-title">Select Themes</h3>
            <button
              className="te-create-btn"
              onClick={() => setIsModalOpen(true)}
            >
              + Create Theme
            </button>
          </div>
          <div className="te-themes-list">
            {themesLoading && themes.length === 0 ? (
              <p>Loading…</p>
            ) : themes.length === 0 ? (
              <p>No themes yet</p>
            ) : (
              themes.map((theme) => (
                <div
                  key={theme.id || theme.themename}
                  className="te-theme-item"
                  onClick={() => handleSelectTheme(theme.themename)}
                >
                  <img
                    src={process.env.REACT_APP_S3_PATH + `${theme.themeImage}`}
                    alt={theme.themename}
                    className="te-theme-image"
                  />

                  {theme.selected === "true" && (
                    <i
                      className="fa-solid fa-check te-selected-icon"
                      title="Selected"
                    />
                  )}

                  <div className="te-icons">
                    <i
                      className="fa-solid fa-image te-icon"
                      title="Edit Image"
                      onClick={(e) => {
                        e.stopPropagation();
                        OpenUpdateModal(theme.themename);
                      }}
                    ></i>
                    <i
                      className="fa-solid fa-gear te-icon"
                      title="Settings"
                      onClick={(e) => {
                        e.stopPropagation();
                        browseTheme(theme.themename);
                      }}
                    />
                    <i
                      className="fa-solid fa-trash te-icon"
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTheme(theme.themename);
                      }}
                    ></i>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Home;
